import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { useNavSections } from '../../navigation/useNavSections';
import { useConnectionStore } from '../../stores/connectionStore';
import { useGlobalStore } from '../../stores/globalStore';
import { useCobrowse } from '../../hooks/useCobrowse';
import SupportSessionIcon from '../common/SupportSessionIcon';
import { supportSessionLabel } from '../common/supportSession';
import { openReportProblem, useReportProblemAvailable } from '../feedback/ReportProblemToolbarButton';
import { PHONE_TABS } from './MobileTabBar';
import './MobileMoreMenu.css';

/**
 * Everything the four tabs could not hold, plus what the status strip used to
 * say.
 *
 * The item list is the sidebar rail's sections, from `useNavSections` (the same
 * hook the sidebar uses), so security, feature visibility and the KDS gate are
 * evaluated once. A second copy of that filtering is how a menu ends up showing
 * someone a screen they cannot open. Each section opens its main page, as the
 * rail does.
 *
 * The four promoted tabs are filtered out: a destination that already has a
 * permanent slot on the bar does not need a second entry three taps deeper.
 *
 * "This session" also holds what the top bar drops on a phone, where it has no
 * room at 360px: the Temp Menu, the support session and Report a problem.
 */
export default function MobileMoreMenu({ onNavigate }: { onNavigate: () => void }) {
  const navigate = useNavigate();
  const { sections } = useNavSections();
  const selectedConnection = useConnectionStore((s) => s.selectedConnection);
  const tempMenuOverrideId = useGlobalStore((s) => s.tempMenuOverrideId);
  const tempMenuOverrideName = useGlobalStore((s) => s.tempMenuOverrideName);
  const reportProblem = useReportProblemAvailable();
  // As the status strip's button: the relay authenticates agents, not customers.
  const noToken = useCallback(async () => null, []);
  const support = useCobrowse(noToken);
  const supportActive = support.status === 'active';
  const supportBusy = support.status === 'connecting' || support.status === 'requesting-consent';

  const promoted = new Set(PHONE_TABS.map((t) => t.route));
  const items = sections
    .filter((s) => !promoted.has(s.railRoute))
    .map((s) => ({ label: s.label, icon: s.icon, route: s.railRoute }));

  const go = (route?: string) => {
    if (!route) return;
    navigate(route);
    onNavigate();
  };

  return (
    <div className="mmm">
      {items.length > 0 && (
        <section className="mmm-group">
          <h2 className="mmm-group-title">Sections</h2>
          <div className="mmm-items">
            {items.map((item) => (
              <button
                key={item.route ?? item.label}
                type="button"
                className="mmm-item"
                onClick={() => go(item.route)}
              >
                <i className={`bi ${item.icon}`} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* What the status strip used to report, now that it does not exist at
          this width. Reference material, so it sits at the bottom. */}
      <section className="mmm-group mmm-status">
        <h2 className="mmm-group-title">This session</h2>
        <button type="button" className="mmm-item" onClick={() => go('/connection?switch=true')}>
          <i className="bi bi-database-fill" aria-hidden="true" />
          <span>{selectedConnection?.facilityName || 'No Connection'}</span>
        </button>
        {tempMenuOverrideId != null && (
          // State, not an action: it is set and cleared in the user menu, as on a desktop.
          <p className="mmm-item mmm-note">
            <i className="bi bi-journal-text" aria-hidden="true" />
            <span>Temp Menu: {tempMenuOverrideName}</span>
          </p>
        )}
        {/* Stays open: the session code shows here once support connects. */}
        <button
          type="button"
          className="mmm-item"
          onClick={() => {
            if (supportBusy) return;
            // Brings the chat back; ending is in the chat. See supportSessionLabel.
            if (supportActive) support.openChat();
            else support.startSession();
          }}
          aria-busy={supportBusy}
        >
          <SupportSessionIcon active={supportActive} />
          <span>{supportSessionLabel(support.status, support.sessionCode)}</span>
        </button>
        {reportProblem && (
          <button
            type="button"
            className="mmm-item"
            data-feedback-ignore="true"
            onClick={() => {
              onNavigate();
              openReportProblem();
            }}
          >
            <i className="bi bi-megaphone-fill" aria-hidden="true" />
            <span>Report a problem with this screen</span>
          </button>
        )}
        <Tooltip title="Dietech Web Application">
          <p className="mmm-version">v{import.meta.env.VITE_APP_VERSION || 'dev'}</p>
        </Tooltip>
      </section>
    </div>
  );
}
